import React, { useState, useEffect } from 'react';
import { X, Save, Camera, Tag, AlertTriangle, Upload, MapPin, Calendar } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

interface Photo {
  id: string;
  nom: string;
  description: string;
  date: string;
  lieu: string;
  tags: string[];
  url: string;
}

interface PhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  photo?: Photo | null;
  onSave: (photo: Omit<Photo, 'id'>) => void;
  mode: 'add' | 'edit';
  enqueteId: string;
}

const PhotoModal: React.FC<PhotoModalProps> = ({ 
  isOpen, 
  onClose, 
  photo, 
  onSave, 
  mode,
  enqueteId 
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    lieu: '',
    tags: [] as string[],
    url: ''
  });
  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  useEffect(() => {
    if (photo && mode === 'edit') {
      setFormData({
        nom: photo.nom,
        description: photo.description,
        date: photo.date,
        lieu: photo.lieu,
        tags: [...photo.tags],
        url: photo.url
      });
      setPreviewUrl(photo.url);
    } else {
      setFormData({
        nom: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        lieu: '',
        tags: [],
        url: ''
      });
      setPreviewUrl('');
    }
    setSelectedFile(null);
    setErrors({});
  }, [photo, mode, isOpen]);

  // Gestion de la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom de la photo est requis';
    }
    
    if (mode === 'add' && !selectedFile) {
      newErrors.file = 'Veuillez sélectionner un fichier';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      if (mode === 'add' && selectedFile) {
        // Upload de la photo vers le serveur avec progress
        try {
          setIsUploading(true);
          setUploadProgress(0);
          
          const formDataToUpload = new FormData();
          formDataToUpload.append('photo', selectedFile);
          formDataToUpload.append('enqueteId', enqueteId);
          formDataToUpload.append('titre', formData.nom);
          formDataToUpload.append('description', formData.description);
          formDataToUpload.append('tags', JSON.stringify(formData.tags));
          formDataToUpload.append('datePrise', formData.date);
          formDataToUpload.append('lieu', formData.lieu);
          
          // Use XMLHttpRequest for progress tracking
          const xhr = new XMLHttpRequest();
          
          const uploadPromise = new Promise<any>((resolve, reject) => {
            xhr.upload.addEventListener('progress', (event) => {
              if (event.lengthComputable) {
                const progress = (event.loaded / event.total) * 100;
                setUploadProgress(Math.round(progress));
              }
            });
            
            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const result = JSON.parse(xhr.responseText);
                  resolve(result);
                } catch {
                  reject(new Error('Invalid JSON response'));
                }
              } else {
                reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
              }
            });
            
            xhr.addEventListener('error', () => {
              reject(new Error('Network error'));
            });
            
            xhr.open('POST', API_ENDPOINTS.UPLOAD_PHOTO);
            xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('accessToken')}`);
            xhr.send(formDataToUpload);
          });
          
          const result = await uploadPromise;
          
          if (result.success) {
            onSave({
              ...formData,
              url: result.data.photo.url
            });
          } else {
            throw new Error(result.message || 'Erreur lors de l\'upload');
          }
        } catch (error) {
          console.error('Erreur lors de l\'upload:', error);
          setErrors(prev => ({ ...prev, file: 'Erreur lors de l\'upload de la photo' }));
        } finally {
          setIsUploading(false);
          setUploadProgress(0);
        }
      } else {
        // Mode édition
        onSave({
          ...formData,
          url: formData.url
        });
      }
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validation du fichier
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, file: 'Le fichier ne doit pas dépasser 10MB' }));
        return;
      }
      
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, file: 'Format de fichier non supporté. Utilisez JPG, PNG, GIF ou WebP' }));
        return;
      }
      
      // Clear previous errors
      setErrors(prev => ({ ...prev, file: '' }));
      
      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setFormData(prev => ({
        ...prev,
        nom: file.name.replace(/\.[^/.]+$/, '') // Remove extension
      }));
      
      // Clean up previous object URL
      return () => URL.revokeObjectURL(objectUrl);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }} onClick={onClose}>
      <div className="w-full max-w-4xl osint-card rounded-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-osint-light-gray">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-osint-orange rounded-lg flex items-center justify-center">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Ajouter une photo' : 'Modifier la photo'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload et prévisualisation */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {mode === 'add' ? 'Sélectionner une photo' : 'Photo'}
              </label>
              
              {mode === 'add' && (
                <div 
                  className="border-2 border-dashed border-osint-light-gray rounded-lg p-6 text-center hover:border-osint-orange transition-colors"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-osint-orange', 'bg-osint-orange/5');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-osint-orange', 'bg-osint-orange/5');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-osint-orange', 'bg-osint-orange/5');
                    const files = e.dataTransfer.files;
                    if (files.length > 0) {
                      const file = files[0];
                      if (file.type.startsWith('image/')) {
                        // Créer un objet FileList compatible
                        const dataTransfer = new DataTransfer();
                        dataTransfer.items.add(file);
                        const event = {
                          target: { files: dataTransfer.files }
                        } as unknown as React.ChangeEvent<HTMLInputElement>;
                        handleFileChange(event);
                      }
                    }
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center space-y-2">
                      <div className="w-16 h-16 bg-osint-light-gray rounded-lg flex items-center justify-center">
                        <Upload className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-400 text-sm">
                        Cliquez ou glissez une image ici
                      </p>
                      <p className="text-gray-500 text-xs">
                        JPG, PNG, GIF, WebP jusqu'à 10MB
                      </p>
                    </div>
                  </label>
                </div>
              )}
              
              {errors.file && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  {errors.file}
                </p>
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-300">Upload en cours...</span>
                    <span className="text-sm text-osint-orange">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-osint-light-gray rounded-full h-2">
                    <div 
                      className="bg-osint-orange h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {/* Prévisualisation */}
              {previewUrl && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-300 mb-2">Aperçu de l'image</p>
                  <div className="relative group">
                    <img
                      src={previewUrl}
                      alt="Aperçu"
                      className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                      onLoad={() => {
                        // Image loaded successfully
                      }}
                      onError={() => {
                        setErrors(prev => ({ ...prev, file: 'Erreur lors du chargement de l\'image' }));
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-lg"></div>
                    {selectedFile && (
                      <div className="absolute bottom-2 left-2 bg-black/80 text-white px-3 py-1 rounded-lg text-xs backdrop-blur-sm">
                        <div className="font-medium">{selectedFile.name}</div>
                        <div className="text-gray-300">
                          {(selectedFile.size / 1024 / 1024).toFixed(1)} MB • {selectedFile.type.split('/')[1].toUpperCase()}
                        </div>
                      </div>
                    )}
                    {mode === 'add' && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          setPreviewUrl('');
                          setFormData(prev => ({ ...prev, nom: '' }));
                        }}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center text-white transition-colors opacity-0 group-hover:opacity-100"
                        title="Supprimer l'image"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Informations */}
            <div className="space-y-6">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nom de la photo *
                </label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                  className={`osint-input w-full ${errors.nom ? 'border-red-500' : ''}`}
                  placeholder="Nom de la photo"
                />
                {errors.nom && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {errors.nom}
                  </p>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Date de prise
                </label>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="osint-input flex-1"
                  />
                </div>
              </div>

              {/* Lieu */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Lieu de prise
                </label>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.lieu}
                    onChange={(e) => setFormData(prev => ({ ...prev, lieu: e.target.value }))}
                    className="osint-input flex-1"
                    placeholder="Lieu de prise de vue"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="osint-input w-full h-24 resize-none"
                  placeholder="Description de la photo..."
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-osint-orange/20 text-osint-orange text-sm rounded-full flex items-center space-x-2"
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="osint-input flex-1"
                    placeholder="Ajouter un tag"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                  >
                    <Tag className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-osint-light-gray flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white text-sm transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>{isUploading ? 'Upload en cours...' : (mode === 'add' ? 'Ajouter' : 'Modifier')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PhotoModal; 